import {useEffect} from 'react'
import {useGetImportStatusQuery} from 'features/Settings/Users/hooks/useGetImportStatusQuery'
import {GetImportStatusResponse, UserImportStatus} from 'interfaces'
import {useActions} from './useActions'
import {PERMISSIONS, withPermission} from 'rbac'

// This is a module-level singleton flag that controls polling across the app.
// Interacts with the import link (copy/click). Normal import polling stops
// once import is complete or re-auth is required, but re-auth link polling
// continues while the external authorization is still pending.
// Note: Only one import polling can be active at a time.
//
// This is intentionally non-reactive. The flag is only consumed inside
// the `refetchInterval` callback, which React Query calls on a timer — it
// does not need to trigger a re-render. A module-level variable is simpler
// and avoids adding a context/external store for a value that React never
// renders directly.
let isPollingActive = false
let shouldPollWhileReauthRequired = false

interface StartImportStatusPollingOptions {
  pollWhileReauthRequired?: boolean
}

const stopImportStatusPolling = (): void => {
  isPollingActive = false
  shouldPollWhileReauthRequired = false
}

export const startImportStatusPolling = ({
  pollWhileReauthRequired = false,
}: StartImportStatusPollingOptions = {}): void => {
  isPollingActive = true
  shouldPollWhileReauthRequired = pollWhileReauthRequired
}

interface UseUsersImportedOptions {
  pollWhileIncomplete?: boolean
}

interface UseUsersImportedResult {
  usersImported: boolean
  isImportStarted: boolean
  reauthRequired: boolean
  lastUpdated: GetImportStatusResponse['lastUpdated']
  authProvider: GetImportStatusResponse['authProvider'] | undefined
  importStatus: GetImportStatusResponse['importStatus'] | undefined
  isLoading: ReturnType<typeof useGetImportStatusQuery>['isLoading']
  isSuccess: ReturnType<typeof useGetImportStatusQuery>['isSuccess']
  isError: ReturnType<typeof useGetImportStatusQuery>['isError']
  refetch: ReturnType<typeof useGetImportStatusQuery>['refetch']
}

export const useUsersImported = (
  options?: UseUsersImportedOptions,
): UseUsersImportedResult => {
  const {isUserLoggedIn} = useActions()
  // Only users with user management read permissions can check the import status.
  // Today SOC Analysts does not have permission to check the import status.
  const canReadImportStatus = withPermission([PERMISSIONS.USER_MANAGEMENT.READ])

  const {data, isLoading, isSuccess, isError, refetch} =
    useGetImportStatusQuery({
      enabled: isUserLoggedIn && canReadImportStatus,
      refetchInterval: options?.pollWhileIncomplete
        ? data => {
            if (!isPollingActive) return false

            const isDone = data?.importStatus === UserImportStatus.DONE
            const isReauth = Boolean(data?.reauthRequired)
            const shouldStopForCompletedImport = isDone && !isReauth
            const shouldStopForRequiredReauth =
              isReauth && !shouldPollWhileReauthRequired
            // Stop normal import polling once the user must reconnect. When
            // polling was started by the reauth link, keep polling so the UI
            // can notice when the external authorization completes.
            if (shouldStopForCompletedImport || shouldStopForRequiredReauth) {
              stopImportStatusPolling()
              return false
            }
            return 10000
          }
        : undefined,
    })

  // Stop polling when a request fails to avoid hammering a failing endpoint
  useEffect(() => {
    if (isError && options?.pollWhileIncomplete) {
      stopImportStatusPolling()
    }
  }, [isError, options?.pollWhileIncomplete])

  // Stop polling when user logs out
  useEffect(() => {
    if (!isUserLoggedIn) {
      stopImportStatusPolling()
    }
  }, [isUserLoggedIn])

  return {
    usersImported: data?.importStatus === UserImportStatus.DONE,
    isImportStarted: data?.importStatus === UserImportStatus.IN_PROGRESS,
    reauthRequired: data?.reauthRequired ?? false,
    lastUpdated: data?.lastUpdated,
    authProvider: data?.authProvider,
    importStatus: data?.importStatus,
    isLoading,
    isSuccess,
    isError,
    refetch,
  }
}
